import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ToastAction } from "@/components/ui/toast";
import { Crown, Info, Loader2, Lock, Settings, Zap, Sparkles, Scissors, Languages } from "lucide-react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useCreateMyLibraryMutation, useCreateMyProjectMutation, useMyBrandKitsQuery, useMyDataQuery } from "@/generated/graphql";
import { useToast } from "@/hooks/use-toast";
import { client } from "@/lib/apollo-client";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import BrandKitScrollRow from "@/components/BrandKitScrollRow";
import { useHasFeature } from "@/lib/useHasFeature";
import { resolveProcessingMode } from '@/lib/upload/resolveProcessingMode';
import ProcessingModeSelector from '@/components/upload/ProcessingModeSelector';
import ClipFormatSelector from '@/components/upload/ClipFormatSelector';
import { DEFAULT_CLIP_FORMAT } from '@/lib/projects/clipFormats';
import { resolveProAccess } from "@/lib/quota/resolveProAccess";
import { sanitizeProCreationSelections } from "@/lib/quota/sanitizeProCreationSelections";
import { isSubscriptionPastDueError } from "@/lib/quota/isSubscriptionPastDueError";
import { useQuotaUpsellDialog } from "@/hooks/use-quota-upsell-dialog";
import { resolvePastDueRecovery } from "@/lib/billing/resolvePastDueRecovery";
import { startStripeCustomerPortal } from "@/lib/billing/startStripeCustomerPortal";
import { resolveLibraryCreatedDestination } from "@/lib/library/resolveLibraryCreatedDestination";
import { Badge } from "@/components/ui/badge";
import { SUBTITLE_LANGUAGES, languageLabel, primaryLanguage } from "@/lib/languages/subtitleLanguages";
import { detectVideoPlatform } from "@/lib/video-metadata";

interface CopyLibraryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  library: {
    name: string;
    base_video_url?: string | null;
    duration_seconds: number;
    format: string;
    language: string;
    // Transcription language of the source. null/undefined means "same as language".
    audio_language?: string | null;
    // 'original' or the YouTube track code the source was built on. null/undefined means original.
    audio_track?: string | null;
    start_time?: number | null;
    end_time?: number | null;
    // The library to set as `source_library` on the new library.
    // - For "copy from community": pass the community library's id.
    // - For "duplicate my library": pass the original's source_library_id (may be null
    //   if the original is user-created — keeps the duplicate in the same tab).
    source_library_id: string | null;
  } | null;
  title?: string;
  isPaidPlan?: boolean;
  cpmUnlocked?: boolean;
}

const LIBRARY_CURATION_LIMIT_ERROR = 'Library curation limit reached';

const isLibraryCurationLimitError = (error: unknown) => (
  error instanceof Error && error.message.includes(LIBRARY_CURATION_LIMIT_ERROR)
);

const CopyLibraryDialog = ({ isOpen, onClose, library, title, isPaidPlan = true, cpmUnlocked = false }: CopyLibraryDialogProps) => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { dialog: quotaUpsellDialog, openQuotaUpsell, handleQuotaError } = useQuotaUpsellDialog({
    onBeforeUpgrade: onClose,
  });

  const [selectedPrompt, setSelectedPrompt] = useState("clipes-virais");
  const [customPrompt, setCustomPrompt] = useState("");
  const [selectedBrandKitId, setSelectedBrandKitId] = useState("none");
  const [selectedFormat, setSelectedFormat] = useState<string>(library?.format || DEFAULT_CLIP_FORMAT);
  const [selectedProcessingMode, setSelectedProcessingMode] = useState("automatic");
  const [removeSilence, setRemoveSilence] = useState(true);

  const hasProFeatures = useHasFeature('pro_features');
  // Pro access: the plan's pro_features flag, or a library unlocked via an
  // active CPM campaign. Gates the same option set as project/library creation.
  const proUnlocked = resolveProAccess({ hasProFeatures, cpmUnlocked });
  const { mode: effectiveProcessingMode, automaticLocked } = resolveProcessingMode({
    isPro: proUnlocked,
    requested: selectedProcessingMode,
  });

  const { data: brandKitsData, loading: brandKitsLoading } = useMyBrandKitsQuery({
    skip: !proUnlocked,
  });
  const { data: myData, loading: myDataLoading, refetch: refetchMyData } = useMyDataQuery();
  const objectiveKnown = !myDataLoading && myData !== undefined;
  const [createMyLibrary, { loading: isCreatingLibrary }] = useCreateMyLibraryMutation();
  const [createMyProject, { loading: isCreatingProject }] = useCreateMyProjectMutation();
  const isCreating = isCreatingLibrary || isCreatingProject;

  // null audio_language means "same as language". Translating is pro-only, so
  // a free user's subtitle language must stay in the audio language's family.
  const effectiveAudioLanguage = library?.audio_language ?? library?.language ?? "pt-BR";
  const canTranslate = hasProFeatures;
  const defaultLanguage = canTranslate ? (library?.language || "pt-BR") : effectiveAudioLanguage;
  // Duplicates of user-created libraries and own_brand copies carry no source id,
  // so the backend only learns the source's audio from these fields. Legacy
  // codes (auto/en/pt) and non-YouTube sources keep the language-only input.
  const sourceAudioFields = library?.base_video_url
    && detectVideoPlatform(library.base_video_url) === 'youtube'
    && SUBTITLE_LANGUAGES.includes(effectiveAudioLanguage)
    ? { audio_language: effectiveAudioLanguage, audio_track: library.audio_track ?? 'original' }
    : {};
  const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);

  // Keep the language default in sync with the source library (keyed on
  // primitive values so it doesn't fight the inline `library` prop identity).
  useEffect(() => {
    setSelectedLanguage(defaultLanguage);
  }, [defaultLanguage]);

  const promptOptions = [
    { id: "clipes-virais", label: t('uploadModal.viralClips'), description: t('uploadModal.viralClipsDesc') },
    { id: "frases-memoraveis", label: t('uploadModal.memorableQuotes'), description: t('uploadModal.memorableQuotesDesc') },
    { id: "clipes-topicos", label: t('uploadModal.topicClips'), description: t('uploadModal.topicClipsDesc') },
    { id: "anuncios-produtos", label: t('uploadModal.productAds'), description: t('uploadModal.productAdsDesc') },
    { id: "depoimento", label: t('uploadModal.testimonials'), description: t('uploadModal.testimonialsDesc') },
    { id: "outro", label: t('uploadModal.other'), description: t('uploadModal.otherDesc') },
  ];

  const proBadge = (
    <RouterLink
      to="/plans"
      onClick={onClose}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 border border-primary/30 text-[10px] font-bold text-primary hover:bg-primary/20 transition-colors"
    >
      <Lock className="w-3 h-3" />
      PRO
    </RouterLink>
  );

  const campaignHint = (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[10px] font-bold text-emerald-500">
      <Sparkles className="w-3 h-3" />
      {t('copyLibrary.unlockedForCampaign')}
    </span>
  );

  const handleConfirm = async () => {
    if (!library || !library.base_video_url || !objectiveKnown) return;

    // Submit-side pro gate: free users can never send pro values.
    const proSelections = sanitizeProCreationSelections({
      promptId: selectedPrompt,
      brandKitId: selectedBrandKitId,
      processingMode: effectiveProcessingMode,
      removeSilence,
    }, proUnlocked);
    const selectedPromptData = promptOptions.find(option => option.id === proSelections.promptId);
    const prompt = proSelections.promptId === "outro" ? customPrompt.trim() : selectedPromptData?.description;

    if (!prompt) return;

    try {
      const input = {
        name: library.name,
        base_video_url: library.base_video_url,
        duration_seconds: library.duration_seconds,
        format: selectedFormat,
        // Defence in depth: a free user never sends a translation.
        language: canTranslate ? selectedLanguage : effectiveAudioLanguage,
        ...sourceAudioFields,
        prompt,
        processing_mode: proSelections.processingMode,
        remove_silence: proSelections.removeSilence,
        ...(library.start_time != null && library.end_time != null ? {
          start_time: library.start_time,
          end_time: library.end_time,
        } : {}),
        ...(proSelections.brandKitId !== "none" ? { brand_kit_id: proSelections.brandKitId } : {}),
      };

      if (myData?.myData?.onboarding_objective === 'own_brand') {
        const result = await createMyProject({ variables: { input } });
        if (result.data?.createMyProject) {
          await client.refetchQueries({ include: ['MyProjects'] });
          await refetchMyData();

          toast({
            title: t('library.copySuccess'),
            description: t('library.copySuccessDescription'),
            duration: 5000,
          });

          onClose();
          navigate(`/project/${result.data.createMyProject.id}/edit`);
        }
        return;
      }

      const result = await createMyLibrary({
        variables: {
          input: {
            ...input,
            ...(library.source_library_id ? { source_library_id: library.source_library_id } : {}),
          },
        },
      });

      if (result.data?.createMyLibrary) {
        await client.refetchQueries({ include: ['MyLibraries'] });
        await refetchMyData();

        toast({
          title: t('library.copySuccess'),
          description: t('library.copySuccessDescription'),
          duration: 5000,
        });

        onClose();
        navigate(resolveLibraryCreatedDestination({
          objective: myData?.myData?.onboarding_objective,
          libraryId: result.data.createMyLibrary.id,
          fallback: '/library',
        }));
      }
    } catch (error) {
      console.error('Failed to copy library:', error);
      if (myData?.myData?.onboarding_objective === 'own_brand' && handleQuotaError(error)) {
        return;
      }
      const isCurationLimitError = isLibraryCurationLimitError(error);
      const isPastDueError = isSubscriptionPastDueError(error);
      if (isCurationLimitError && !cpmUnlocked) {
        openQuotaUpsell('library_curation');
        return;
      }
      const fallbackMessage = error instanceof Error ? error.message : '';
      const description = isPastDueError
        ? t('library.pastDueErrorDescription')
        : isCurationLimitError
          ? t('library.copyErrorDescription')
          : fallbackMessage || t('library.copyErrorDescription');

      toast({
        title: isPastDueError
          ? t('library.pastDueErrorTitle')
          : t('library.copyError'),
        description,
        variant: "destructive",
        ...(isPastDueError ? {
          action: (
            <ToastAction
              altText={t('library.payNow')}
              className="gap-1"
              onClick={async () => {
                onClose();
                const recovery = resolvePastDueRecovery(myData?.myData?.subscription);
                if (recovery.kind === 'store_manage') {
                  window.open(recovery.url, '_blank', 'noopener,noreferrer');
                  return;
                }
                if (recovery.kind !== 'stripe_portal') {
                  navigate('/invoice');
                  return;
                }
                try {
                  const url = await startStripeCustomerPortal(window.location.href, {
                    preferOpenInvoice: true,
                  });
                  window.location.href = url;
                } catch (portalError) {
                  console.error('Failed to open Stripe customer portal:', portalError);
                  toast({
                    title: t('library.pastDueErrorTitle'),
                    description: t('library.pastDuePortalError'),
                    variant: 'destructive',
                  });
                }
              }}
            >
              {t('library.payNow')}
            </ToastAction>
          ),
        } : {}),
      });
    }
  };

  const canSubmit = selectedPrompt !== "outro" || customPrompt.trim().length > 0;
  // The source video URL is required to generate clips. Some libraries (e.g.
  // campaign "Vídeos Elegíveis" curated from a youtube-download) can reach this
  // dialog without one; without this guard `handleConfirm` returns silently and
  // the button looks broken. Surface it instead of no-op'ing.
  const hasVideoUrl = !!library?.base_video_url;

  return (
    <>
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle>{title || t('library.copyDialogTitle')}</DialogTitle>
          <DialogDescription>
            {t('library.copyDialogDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2 min-w-0">
          <ClipFormatSelector value={selectedFormat} onChange={setSelectedFormat} />

          {/* Subtitle language selector (other languages are a PRO translation) */}
          <div className="space-y-3">
            <label className="text-sm font-semibold flex items-center gap-2 text-foreground">
              <Languages className="w-4 h-4 text-primary" />
              {t('uploadModal.subtitleLanguage')}
            </label>
            <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
              <SelectTrigger className="h-10 text-sm text-left shadow-sm border-primary/20 focus:border-primary/40 transition-all duration-300">
                <SelectValue placeholder={t('uploadModal.selectLanguage')} />
              </SelectTrigger>
              <SelectContent>
                {SUBTITLE_LANGUAGES.map((code) => {
                  const locked = !canTranslate && primaryLanguage(code) !== primaryLanguage(effectiveAudioLanguage);
                  return (
                    <SelectItem key={code} value={code} disabled={locked}>
                      <div className="flex items-center gap-2">
                        <span>{languageLabel(code, i18n?.language ?? 'en')}</span>
                        {locked && (
                          <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/30">
                            <Crown className="w-3 h-3 mr-1" />
                            PRO
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Processing Mode Selector — Copiloto is free, Automático is PRO */}
          <div className="space-y-3">
            <label className="text-sm font-semibold flex items-center gap-2 text-foreground">
              <Zap className="w-4 h-4 text-primary" />
              {t('copyLibrary.processingMode')}
              {!isPaidPlan && cpmUnlocked && campaignHint}
            </label>
            <ProcessingModeSelector
              value={effectiveProcessingMode}
              onChange={setSelectedProcessingMode}
              automaticLocked={automaticLocked}
            />
          </div>

          {/* Prompt Selector */}
          <div className="space-y-4">
            <label className="text-sm font-semibold flex items-center gap-2 text-foreground">
              <Crown className="w-4 h-4 text-primary" />
              {t('uploadModalExtra.whatYouLookFor')}
              {!isPaidPlan && cpmUnlocked && campaignHint}
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {promptOptions.map((option) => {
                const isProOption = option.id !== "clipes-virais";
                const isDisabled = isProOption && !proUnlocked;
                return (
                <div
                  key={option.id}
                  className={`p-3 rounded-xl border-2 transition-all duration-300 relative ${
                    isDisabled
                      ? 'opacity-50 cursor-not-allowed border-border bg-background/50'
                      : selectedPrompt === option.id
                        ? 'border-primary bg-gradient-to-br from-primary/10 to-primary/5 shadow-glow cursor-pointer hover:scale-105'
                        : 'border-border hover:border-primary/40 bg-background/50 cursor-pointer hover:scale-105'
                  }`}
                  onClick={() => { if (!isDisabled) setSelectedPrompt(option.id); }}
                >
                  <h3 className="font-semibold text-xs text-foreground flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1">{option.label}{isDisabled && proBadge}</span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-4 h-4 text-muted-foreground hover:text-primary cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-xs max-w-xs">{option.description}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </h3>
                </div>
                );
              })}
            </div>

            {/* Custom Prompt Input */}
            {selectedPrompt === "outro" && (
              <div className={`space-y-3 ${!proUnlocked ? 'pointer-events-none opacity-50' : ''}`}>
                <label className="text-sm font-semibold flex items-center gap-2 text-foreground">
                  <Settings className="w-4 h-4 text-primary" />
                  {t('uploadModalExtra.describeWhatYouLookFor')}
                </label>
                <textarea
                  placeholder={t('uploadModalExtra.customPromptPlaceholder')}
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  className="min-h-[100px] w-full rounded-md border border-primary/20 bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 shadow-sm focus:shadow-glow focus:border-primary/40 transition-all duration-300"
                  rows={4}
                />
                {customPrompt.trim() === "" && (
                  <p className="text-xs text-muted-foreground">
                    {t('uploadModalExtra.customPromptHint')}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Brand Kit Selection (pro) */}
          <div className="space-y-3">
            <label className="text-sm font-semibold flex items-center gap-2 text-foreground">
              <Crown className="w-4 h-4 text-primary" />
              {t('uploadModal.brandKit')}
              {!proUnlocked && proBadge}
            </label>
            {!proUnlocked ? (
              <div className="opacity-60 cursor-not-allowed bg-muted rounded-md p-3 text-sm text-center text-muted-foreground">
                {t('uploadModal.brandKitProOnly')}
              </div>
            ) : brandKitsLoading ? (
              <div className="h-10 border border-primary/20 rounded-md flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                <span className="ml-2 text-sm text-muted-foreground">{t('uploadModal.loadingBrandKits')}</span>
              </div>
            ) : (() => {
                const allBrandKits = brandKitsData?.myBrandKits || [];

                return (
                  <div className="space-y-3">
                    <div
                      className={`cursor-pointer rounded-lg border-2 transition-colors p-2 text-center text-sm ${
                        selectedBrandKitId === "none"
                          ? 'border-primary bg-primary/10'
                          : 'border-gray-600 hover:border-gray-400'
                      }`}
                      onClick={() => setSelectedBrandKitId("none")}
                    >
                      {t('uploadModal.noBrandKit')}
                    </div>
                    {allBrandKits.length > 0 && (
                      <BrandKitScrollRow
                        kits={allBrandKits}
                        selectedId={selectedBrandKitId}
                        onSelect={setSelectedBrandKitId}
                        showDefault
                      />
                    )}
                  </div>
                );
              })()
            }
          </div>

          {/* Remove Silence (pro) */}
          <div className="flex items-center justify-between gap-4 rounded-xl border-2 border-border bg-background/50 p-3">
            <div className="flex-1">
              <label
                htmlFor="copy-remove-silence"
                className="text-sm font-semibold mb-1 flex items-center gap-2 text-foreground"
              >
                <Scissors className="w-4 h-4 text-primary" />
                {t('uploadModal.removeSilence')}
                {!proUnlocked && proBadge}
              </label>
              <p className="text-xs text-muted-foreground">
                {t('uploadModal.removeSilenceDescription')}
              </p>
            </div>
            <Switch
              id="copy-remove-silence"
              checked={proUnlocked && removeSilence}
              onCheckedChange={setRemoveSilence}
              disabled={!proUnlocked}
            />
          </div>
        </div>

        {library && !hasVideoUrl && (
          <p className="text-xs text-destructive text-center px-2">
            {t('library.videoUnavailableForClips')}
          </p>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isCreating}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="gradient"
            onClick={handleConfirm}
            disabled={isCreating || !canSubmit || !hasVideoUrl || !objectiveKnown}
          >
            {isCreating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                {t('library.copying')}
              </>
            ) : (
              t('library.copyConfirm')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {quotaUpsellDialog}
    </>
  );
};

export default CopyLibraryDialog;
